/*
  Quiz results page (sections/quiz-results.liquid).

  Reads the answers the quiz put in the query string
  (?name=&area=&goal=&chip=&...&rec=<handle>&alt=<handle>), personalises the
  headline, builds the "what you told us" summary, and moves the matching
  product card(s) out of the hidden bank into the primary / also-helps slots.
  The product cards are rendered by Liquid (one block per product) so price,
  image and add-to-cart come from Shopify, not from the URL.
*/
(function () {
  'use strict';

  /* Same ten problems as the homepage review filter and the quiz. */
  var PILL_LABELS = {
    'slices-hooks': 'Slices & Hooks', 'inconsistent-contact': 'Inconsistent Contact', 'distance-power': 'Distance & Power',
    'flipping-casting': 'Flipping & Casting', 'short-putts': 'Missed Short Putts', 'three-putts': 'Three-Putts & Speed',
    'green-reading': 'Reading Greens', 'alignment-aim': 'Alignment & Aim', 'chipping': 'Chipping & Pitching', 'bunkers': 'Bunker Play'
  };
  function pillLabel(slug) { return PILL_LABELS[slug] || slug; }

  /* Labels for the summary rows, in display order. */
  var SUMMARY = [
    { key: 'problem', label: 'Costing you the most strokes', map: pillLabel },
    { key: 'also', label: 'Also working on', map: pillLabel },
    { key: 'area', label: 'Needs the most help' },
    { key: 'goal', label: 'Full swing goal' },
    { key: 'sg', label: 'Short game focus' },
    { key: 'chip', label: 'Bad chip usually' },
    { key: 'bunker', label: 'Bad bunker shot usually' },
    { key: 'putt', label: 'Bad putt usually' },
    { key: 'dir', label: '7-iron direction miss' },
    { key: 'contact_miss', label: '7-iron contact miss' },
    { key: 'driver', label: 'Driver distance' },
    { key: 'faults', label: 'Swing characteristics' },
    { key: 'hcp', label: 'Handicap' }
  ];

  /* One line per product explaining the fit in the golfer's own answer.
     First match wins; `when` is {question: answer} (any answer if omitted). */
  var REASONS = {
    'putting-thing': [
      { when: { problem: 'short-putts' }, text: 'Short putts are a start-line problem. The Putting Thing’s putter gate and ball gate show you the instant the face or path is off, so a four-footer stops being a coin flip.' },
      { when: { problem: 'three-putts' }, text: 'Three-putts come from speed, and speed comes from a repeatable stroke. The Putting Thing’s stroke trainer and gates build the tempo and centre contact that make distance predictable.' },
      { when: { putt: 'Pull' }, text: 'A pulled putt starts left of your line. The Putting Thing’s putter gate and ball gate make that start-line miss obvious on the very next stroke.' },
      { when: { putt: 'Push' }, text: 'A pushed putt starts right of your line. The Putting Thing’s adjustable gates give instant feedback on face and path so you can train the start line.' },
      { when: { putt: 'No feel for distance' }, text: 'Distance control comes from a repeatable stroke. The Putting Thing combines a mirror, gates and a stroke trainer so your tempo and contact stop wandering.' },
      { text: 'Three Tour-proven putting tools in one: mirror, gates and stroke trainer, so every practice putt tells you something.' }
    ],
    'slope-plate': [
      { when: { problem: 'green-reading' }, text: 'You said reading greens. The Slope Plate trains your feet to feel break the way Tour caddies do, so you stop guessing at the line.' },
      { when: { problem: 'three-putts' }, text: 'Long putts die or race past when you misjudge the slope. The Slope Plate trains the feel for break that turns a three-putt into a tap-in.' },
      { when: { problem: 'short-putts' }, text: 'Even short putts break. The Slope Plate gives you the feel for slope so you stop pushing the ones you thought were straight.' },
      { when: { putt: 'Misread' }, text: 'You said misreads are the usual culprit. The Slope Plate trains your feet to feel break the way Tour caddies do, so you stop guessing at the line.' },
      { text: 'Trains your feet and brain to feel slope on the green, which is where most misreads start.' }
    ],
    'green-cards': [
      { when: { problem: 'green-reading' }, text: 'Green Cards tell you exactly where to aim for any slope and green speed, so a read turns into a number, not a guess.' },
      { when: { putt: 'Misread' }, text: 'Green Cards tell you exactly where to aim for any slope and green speed, so a misread turns into a number, not a guess.' },
      { text: 'Tour-data aim points for every slope and speed. The putting cheat sheet.' }
    ],
    'putting-system': [
      { text: 'When everything on the greens goes wrong, fix the stroke, the read and the aim together: Putting Thing, Slope Plate and Green Cards in one system.' }
    ],
    'pressure-plate': [
      { when: { problem: 'distance-power' }, text: 'Distance comes from the ground up. The Pressure Plate, with the Power Wedge included, teaches the load into the trail side and the push to the lead side that turn into clubhead speed.' },
      { when: { problem: 'inconsistent-contact' }, text: 'Inconsistent contact is usually weight stuck on the trail side at impact. The Pressure Plate tells you on every swing whether you actually got to your lead side, so the low point moves in front of the ball.' },
      { when: { problem: 'flipping-casting' }, text: 'A flip is often the hands rescuing a weight shift that never happened. The Pressure Plate fixes the shift so the wrists stop compensating.' },
      { when: { faults: 'Shifting Weight/Pressure' }, text: 'You flagged weight and pressure shift. The Pressure Plate tells you, on every swing, whether you actually loaded into the trail side and got to your lead side before impact.' },
      { when: { faults: 'Swaying' }, text: 'Swaying is a weight shift that slides instead of loads. The Pressure Plate gives you the feel of loading in place so the sway disappears.' },
      { when: { goal: 'Increasing Distance' }, text: 'Distance comes from the ground up. The Pressure Plate, with the Power Wedge included, teaches the load and push that turns into clubhead speed.' },
      { when: { contact_miss: 'Fat' }, text: 'A fat shot is usually weight stuck on the trail side at impact. The Pressure Plate trains getting to your lead side so the low point moves in front of the ball.' },
      { text: 'Feel the correct weight and pressure shift on every swing, with the Power Wedge included for the trail-side load.' }
    ],
    'wrist-x': [
      { when: { problem: 'flipping-casting' }, text: 'Flipping and casting happen at the wrists, and they are too fast to feel. Wrist-X clicks the instant your wrist crosses the angle you set, so you know exactly when it happens and can train it out.' },
      { when: { problem: 'inconsistent-contact' }, text: 'Chunks and blades start at the wrists. Wrist-X gives you an audible click the moment your wrist releases early, so consistent contact becomes something you can feel and repeat.' },
      { when: { problem: 'chipping' }, text: 'Bladed and chunked chips come from a scooping lead wrist. Wrist-X clicks when that wrist breaks down so you feel the difference on every chip and pitch.' },
      { when: { problem: 'distance-power' }, text: 'Clubhead speed is generated by the wrists. Wrist-X lets you train the set and release that add speed without swinging harder.' },
      { when: { faults: 'Casting/Flipping Early' }, text: 'Casting and flipping start at the wrists, and they are too fast to feel. Wrist-X clicks the instant your wrist crosses the angle you set, so you know exactly when it happens.' },
      { when: { contact_miss: 'Thin' }, text: 'Thin contact usually means the wrists released early. Wrist-X gives you an audible click the moment that happens so you can train it out.' },
      { when: { chip: 'Thin' }, text: 'Bladed chips come from a scooping wrist. Wrist-X clicks when your lead wrist breaks down so you can feel the difference on every chip.' },
      { when: { dir: 'Hook' }, text: 'A hook is a face that closes too fast, and the face listens to your wrists. Wrist-X lets you hear the wrist position that controls it.' },
      { text: 'Chunks, blades, hooks and slices all start at the wrists. Wrist-X clicks the moment your wrist passes the angle you set, so you get real-time feedback instead of guesswork.' }
    ],
    'alignment-mirror': [
      { when: { problem: 'alignment-aim' }, text: 'You said alignment and aim. The Alignment Mirror shows your shoulders, hips, posture and head position at address, the setup changes you cannot feel but that move every shot.' },
      { when: { problem: 'slices-hooks' }, text: 'A two-way miss usually starts at setup. The Alignment Mirror shows your real body lines before you swing so the fix starts at address.' },
      { when: { faults: 'Swaying' }, text: 'Head sway is the fault Alignment Mirror owners name most. See your head, shoulders and hips move off the ball the instant it happens.' },
      { when: { dir: 'Could be any of the above' }, text: 'When the miss goes both ways, setup is the usual suspect. The Alignment Mirror shows your body lines, posture and head position before you swing.' },
      { when: { dir: 'Push' }, text: 'Pushes often start with shoulders aimed right of the target without you feeling it. The Alignment Mirror shows your real lines at address.' },
      { text: 'Your setup changes a little every day and you cannot feel it. The Alignment Mirror shows body lines, posture and head position so your fundamentals stay put.' }
    ],
    'alignment-disc': [
      { when: { problem: 'slices-hooks' }, text: 'A slice or hook is a path problem before it is a face problem. Alignment Discs hold rods at precise angles so you can train the path that turns a slice into a draw and see it on every swing.' },
      { when: { problem: 'alignment-aim' }, text: 'Alignment Discs give you a repeatable station for aim, takeaway and path, set at exact angles every time.' },
      { when: { faults: 'Over the Top' }, text: 'Over the top is a path problem. Alignment Discs hold rods at precise angles so you can train an inside path and see the fix on every swing.' },
      { when: { dir: 'Slice' }, text: 'A slice is an out-to-in path with an open face. Alignment Discs set rods at exact angles to train the path that turns a slice into a draw.' },
      { when: { dir: 'Pull' }, text: 'A pull is a path that comes across the ball. Alignment Discs give you a repeatable station for takeaway, path and plane.' },
      { text: 'Place alignment rods anywhere at precise angles, every time, to train takeaway, path, plane and setup.' }
    ],
    'chipping-plate': [
      { when: { problem: 'chipping' }, text: 'Chunked and bladed chips come from weight hanging back. The Chipping Plate tilts the moment less than two thirds of your weight is on your lead side, so the fix is instant.' },
      { when: { problem: 'bunkers' }, text: 'Bunker shots need the same lead-side weight as a chip. The Chipping Plate trains it anywhere, sand or not.' },
      { when: { chip: 'Chunk' }, text: 'Chunked chips come from weight hanging back. The Chipping Plate tilts the moment less than two thirds of your weight is on your lead side, so the fix is instant.' },
      { when: { chip: 'I suck at everything' }, text: 'When chips go everywhere, start with weight. The Chipping Plate gives foolproof feedback that your weight is forward through the whole motion.' },
      { text: 'Keeps two thirds of your weight on the lead side through every chip, and tilts the moment it is not.' }
    ],
    'bunker-mate': [
      { when: { problem: 'bunkers' }, text: 'You said bunker play. Bunker Mate is a portable bunker simulator: dig with the leading edge or hit too far behind the ball and you know right away, no sand required.' },
      { when: { bunker: 'Left it in the bunker' }, text: 'Leaving it in the sand usually means digging with the leading edge. Bunker Mate tells you right away when you dig or hit too far behind the ball.' },
      { when: { bunker: 'Skull over the green' }, text: 'A skulled bunker shot is contact too close to the ball. Bunker Mate lets you practice the right entry point anywhere, no sand required.' },
      { text: 'A portable bunker simulator that gives you real feedback on entry point and technique, no sand needed.' }
    ]
  };

  function params() {
    var out = {};
    var qs = new URLSearchParams(window.location.search);
    qs.forEach(function (v, k) { out[k] = String(v).trim(); });
    return out;
  }

  function matches(when, p) {
    if (!when) return true;
    return Object.keys(when).every(function (q) {
      var v = p[q] || '';
      if (q === 'faults') return v.split(',').map(function (s) { return s.trim(); }).indexOf(when[q]) !== -1;
      return v === when[q];
    });
  }

  function reasonFor(handle, p) {
    var list = REASONS[handle] || [];
    for (var i = 0; i < list.length; i++) if (matches(list[i].when, p)) return list[i].text;
    return '';
  }

  /* Move a customer review into the card: it must be about this card's
     product, and about the golfer's problem (or one of their extra picks).
     A review about a different product would mislead, so no match means no
     review. */
  function placeReview(root, card, slugs, handle) {
    var slot = card.querySelector('[data-card-review]');
    var bank = root.querySelector('[data-reviews-bank]');
    if (!slot || !bank || !slugs.length) return;
    var pool = [].slice.call(bank.querySelectorAll('[data-review]'));
    var pick = null;
    slugs.some(function (slug) {
      pick = pool.filter(function (r) { return r.getAttribute('data-cat') === slug && r.getAttribute('data-product') === handle; })[0] || null;
      return !!pick;
    });
    if (!pick) return;
    pick.hidden = false;
    slot.appendChild(pick);
    slot.hidden = false;
  }

  function capitalise(s) {
    s = String(s || '').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }

  function init() {
    document.querySelectorAll('[data-quiz-results]').forEach(function (root) {
      if (root.__done) return;
      root.__done = true;

      var p = params();
      var bank = root.querySelector('[data-results-bank]');
      var primarySlot = root.querySelector('[data-results-primary]');
      var altSlot = root.querySelector('[data-results-alt]');
      var altWrap = root.querySelector('[data-results-alt-wrap]');
      var empty = root.querySelector('[data-results-empty]');
      var body = root.querySelector('[data-results-body]');

      var primary = p.rec && bank ? bank.querySelector('[data-handle="' + CSS.escape(p.rec) + '"]') : null;

      if (!primary) {
        if (empty) empty.hidden = false;
        if (body) body.hidden = true;
        return;
      }

      /* Headline */
      var name = capitalise(p.name);
      var heading = root.querySelector('[data-results-heading]');
      if (heading) {
        var tpl = name ? heading.getAttribute('data-with-name') : heading.getAttribute('data-without-name');
        heading.textContent = (tpl || '').replace('{name}', name);
      }

      /* Summary rows */
      var summary = root.querySelector('[data-results-summary]');
      if (summary) {
        SUMMARY.forEach(function (row) {
          var v = p[row.key];
          if (!v) return;
          var dt = document.createElement('dt'); dt.textContent = row.label;
          var dd = document.createElement('dd');
          dd.textContent = row.map ? v.split(',').map(function (x) { return row.map(x.trim()); }).join(', ') : v.replace(/,/g, ', ');
          summary.appendChild(dt); summary.appendChild(dd);
        });
      }

      /* Primary card */
      primary.hidden = false;
      primary.classList.add('quiz-results__card--primary');
      var why = primary.querySelector('[data-card-reason]');
      var reason = reasonFor(p.rec, p);
      if (why && reason) { why.textContent = reason; why.hidden = false; }
      primarySlot.appendChild(primary);
      placeReview(root, primary, p.problem ? [p.problem] : [], p.rec);

      /* Also-helps card */
      var alt = p.alt && p.alt !== p.rec && bank ? bank.querySelector('[data-handle="' + CSS.escape(p.alt) + '"]') : null;
      if (alt && altSlot) {
        alt.hidden = false;
        alt.classList.add('quiz-results__card--alt');
        var altWhy = alt.querySelector('[data-card-reason]');
        var altReason = reasonFor(p.alt, p);
        if (altWhy && altReason) { altWhy.textContent = altReason; altWhy.hidden = false; }
        altSlot.appendChild(alt);
        placeReview(root, alt, (p.also || '').split(',').concat([p.problem || '']).filter(Boolean), p.alt);
        if (altWrap) altWrap.hidden = false;
      }

      /* Anything left in the banks is not needed on this visit. */
      if (bank) bank.remove();
      var rbank = root.querySelector('[data-reviews-bank]');
      if (rbank) rbank.remove();
      if (body) body.hidden = false;
      if (empty) empty.hidden = true;
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
